import { supabase } from '../lib/supabase'
import { stepWhen } from './clientDates'
import type { WriteResult } from './projectWrites'

/**
 * THE PROJECT TRACKER — one row per step, in `order_index` order, grouped on
 * the client page by `phase`.
 *
 * Built 9 Oct 2026. It exists because Marjan asked for "each step and owners
 * of the task including timelines".
 *
 * **Both dates are nullable on purpose.** An undated step draws "to be
 * scheduled" rather than a guess. The gaps are the most useful thing on it.
 *
 * Reads and writes go through the same row-level security as deliverables:
 * `has_project_access(project_id)` to read, `is_portal_staff()` to write. The
 * standing rule in this database is that **a new table is unreachable until
 * it is granted, whatever its RLS says** — a column added later inherits the
 * table-level grant, checked with `has_column_privilege` each time rather
 * than assumed.
 *
 * **A dead column.** `done` still exists on the table, added and replaced by
 * `status` on 14 Oct 2026; the DROP timed out against a lock on a live table.
 * Nothing reads or writes it. Drop it the next quiet moment.
 */

/** Who the step belongs to. `target` is a date nobody owns — a go-live. */
export type StepOwner = 'portal' | 'client' | 'both' | 'target'

export const STEP_OWNERS: StepOwner[] = ['portal', 'client', 'both', 'target']

/**
 * WHERE A STEP STANDS — said, not inferred. 14 Oct 2026.
 *
 * It was briefly a single `done` tick, on the argument that dates already
 * carry most of a status. Tom overruled it after seeing the Word tracker he
 * and Marjan had been keeping: the five words were already the shared
 * vocabulary between the two companies, and a tracker that quietly renames
 * what both sides already say is a tracker nobody trusts. Replaced rather
 * than added to — two fields that both claim to say where a step stands will
 * disagree within a week.
 */
export type StepStatus = 'not_started' | 'in_progress' | 'awaiting_client' | 'needs_follow_up' | 'done'

export const STEP_STATUSES: StepStatus[] = [
  'not_started',
  'in_progress',
  'awaiting_client',
  'needs_follow_up',
  'done',
]

/** Which stage of the job. The client page groups by it. */
export type StepPhase = 'pre' | 'production' | 'delivery'

export const STEP_PHASES: StepPhase[] = ['pre', 'production', 'delivery']

export function phaseLabel(phase: StepPhase): string {
  switch (phase) {
    case 'pre':
      return 'Pre-production'
    case 'production':
      return 'Production'
    case 'delivery':
      return 'Delivery'
  }
}

export const PHASE_PICKER: { value: StepPhase; label: string }[] = STEP_PHASES.map((value) => ({
  value,
  label: phaseLabel(value),
}))

export interface ProjectStep {
  id: string
  title: string
  note: string
  owner: StepOwner
  /** The specific person or team, e.g. "Marjan — Fadi / Jehangir". '' falls back to the owner label. */
  ownerName: string
  status: StepStatus
  phase: StepPhase
  /** ISO date, or '' when unscheduled. */
  start: string
  /** ISO date for a run of days, or '' for a single day or an unscheduled step. */
  end: string
  order: number
}

/** The status in the client's own words. */
export function statusLabel(status: StepStatus, clientName: string): string {
  switch (status) {
    case 'not_started':
      return 'Not started'
    case 'in_progress':
      return 'In progress'
    case 'awaiting_client':
      return `Awaiting ${clientName.trim() || 'the client'}`
    case 'needs_follow_up':
      return 'Needs follow-up'
    case 'done':
      return 'Done'
  }
}

/** The studio's picker. Generic wording — the client's name is filled in on the page. */
export const STATUS_PICKER: { value: StepStatus; label: string }[] = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'awaiting_client', label: 'Awaiting the client' },
  { value: 'needs_follow_up', label: 'Needs follow-up' },
  { value: 'done', label: 'Done' },
]

/**
 * WHAT A ROW SHOULD WEIGH. Three tones, not five.
 *
 * `done` goes quiet, `attention` comes forward, everything else sits in the
 * middle. The words carry the five-way detail; the weight carries the glance
 * down the page, which is the job the old spine used to do.
 *
 * The date keeps one job of its own: a step whose last date has passed and
 * which is not marked done is attention whatever its status says. That is the
 * fault the status field was added to fix — a passed step used to read
 * exactly like a finished one.
 */
export type StepTone = 'done' | 'attention' | 'normal'

export function stepTone(step: ProjectStep, today: Date = new Date()): StepTone {
  if (step.status === 'done') return 'done'
  if (step.status === 'awaiting_client' || step.status === 'needs_follow_up') return 'attention'
  if (stepWhen(step.start, step.end, today) === 'past') return 'attention'
  return 'normal'
}

/**
 * Who it says on the page — the specific name where there is one, otherwise
 * the generic label. The client's own name rather than the word "client": a
 * tracker that says MARJAN against the steps Marjan owns is doing its job;
 * one that says "Client" is a form.
 */
export function ownerLabel(owner: StepOwner, clientName: string): string {
  const them = clientName.trim() || 'Client'
  switch (owner) {
    case 'portal':
      return 'Portal'
    case 'client':
      return them
    case 'both':
      return `Portal & ${them}`
    case 'target':
      return 'Target'
  }
}

export function ownerDisplay(step: ProjectStep, clientName: string): string {
  return step.ownerName.trim() || ownerLabel(step.owner, clientName)
}

/** The studio's own words for the four, in the order they appear in the picker. */
export const OWNER_PICKER: { value: StepOwner; label: string }[] = [
  { value: 'portal', label: 'Portal' },
  { value: 'client', label: 'The client' },
  { value: 'both', label: 'Both' },
  { value: 'target', label: 'A target date' },
]

/**
 * The steps in phase order, empty phases dropped. A project whose steps all
 * sit in one phase comes back as a single group, and the page then draws no
 * headings at all — grouping a short tracker is furniture.
 */
export function byPhase(
  steps: ProjectStep[],
): { phase: StepPhase; label: string; steps: ProjectStep[] }[] {
  return STEP_PHASES.map((phase) => ({
    phase,
    label: phaseLabel(phase),
    steps: steps.filter((step) => step.phase === phase),
  })).filter((group) => group.steps.length > 0)
}

/** "1 of 14 done · 11 with MARJAN" — the line beside a phase heading. */
export function phaseSummary(steps: ProjectStep[], clientName: string): string {
  const done = steps.filter((step) => step.status === 'done').length
  const waiting = steps.filter((step) => step.status === 'awaiting_client').length
  const parts = [`${done} of ${steps.length} done`]
  if (waiting > 0) parts.push(`${waiting} with ${clientName.trim() || 'the client'}`)
  return parts.join(' · ')
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function fail(error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `project_steps: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

function stepOf(row: Record<string, unknown>): ProjectStep {
  const owner = text(row.owner) as StepOwner
  const status = text(row.status) as StepStatus
  const phase = text(row.phase) as StepPhase
  return {
    id: String(row.id ?? ''),
    title: text(row.title),
    note: text(row.note),
    owner: STEP_OWNERS.includes(owner) ? owner : 'portal',
    ownerName: text(row.owner_name),
    status: STEP_STATUSES.includes(status) ? status : 'not_started',
    phase: STEP_PHASES.includes(phase) ? phase : 'pre',
    start: text(row.start_date).slice(0, 10),
    end: text(row.end_date).slice(0, 10),
    order: Number(row.order_index ?? 0),
  }
}

const COLUMNS =
  'id, project_id, title, note, owner, owner_name, status, phase, start_date, end_date, order_index'

/** Every step on one project, in the order the studio put them in. */
export async function fetchProjectSteps(projectId: string): Promise<ProjectStep[]> {
  if (!supabase || !projectId) return []

  const { data, error } = await supabase
    .from('project_steps')
    .select(COLUMNS)
    .eq('project_id', projectId)
    .order('order_index', { ascending: true })

  if (error) {
    console.warn(`[supabase] project steps load failed: ${error.message}`)
    return []
  }
  return (data ?? []).map((row) => stepOf(row as Record<string, unknown>))
}

export interface StepDraft {
  title: string
  note: string
  owner: StepOwner
  ownerName: string
  phase: StepPhase
  start: string
  end: string
}

function rowOf(draft: StepDraft) {
  return {
    title: draft.title.trim(),
    note: draft.note.trim() || null,
    owner: draft.owner,
    owner_name: draft.ownerName.trim() || null,
    phase: draft.phase,
    start_date: draft.start || null,
    /* A run that ends on its start date is just a day — stored as one, so
       nothing downstream has to special-case the equal case. Same rule as
       project_shoots. */
    end_date: draft.end && draft.end > draft.start ? draft.end : null,
  }
}

export async function createStep(
  projectId: string,
  draft: StepDraft,
  order: number,
): Promise<{ result: WriteResult; step?: ProjectStep }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase
    .from('project_steps')
    .insert({ project_id: projectId, order_index: order, ...rowOf(draft) })
    .select(COLUMNS)
    .single()

  const result = fail(error)
  if (!result.ok || !data) return { result }
  return { result, step: stepOf(data as Record<string, unknown>) }
}

export async function updateStep(id: string, draft: StepDraft): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase
    .from('project_steps')
    .update({ ...rowOf(draft), updated_at: new Date().toISOString() })
    .eq('id', id)
  return fail(error)
}

/** The status. Its own write, because it is one field and one pick. */
export async function setStepStatus(id: string, status: StepStatus): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase
    .from('project_steps')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id)
  return fail(error)
}

export async function deleteStep(id: string): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase.from('project_steps').delete().eq('id', id)
  return fail(error)
}

/**
 * Write the running order back after a move. One update per row rather than an
 * upsert: `.upsert()` compiles to `INSERT … ON CONFLICT DO UPDATE`, which
 * Postgres requires the INSERT privilege for even when it only ever updates,
 * and that trap has already cost this codebase a silent failure once (see the
 * Drive link dismissals, 7 Oct 2026).
 */
export async function setStepOrder(ids: string[]): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  for (let index = 0; index < ids.length; index += 1) {
    const { error } = await supabase
      .from('project_steps')
      .update({ order_index: index })
      .eq('id', ids[index])
    if (error) return fail(error)
  }
  return { ok: true }
}
